import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Send, Loader2, Mail, Users } from "lucide-react";
import AdminPageHeader from "./AdminPageHeader";

interface BroadcastLog {
  id: string;
  subject: string;
  recipient_count: number;
  success_count: number;
  failed_count: number;
  created_at: string;
  audience_filter: any;
}

const BroadcastEmails = () => {
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [plan, setPlan] = useState<string>("all");
  const [testEmail, setTestEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [logs, setLogs] = useState<BroadcastLog[]>([]);
  const [unsubCount, setUnsubCount] = useState(0);
  const [profilesCount, setProfilesCount] = useState(0);

  const syncToResend = async () => {
    setSyncing(true);
    try {
      const { data, error } = await supabase.functions.invoke("sync-resend-contacts");
      if (error) throw error;
      if (!data?.ok) throw new Error(data?.error || "Failed");
      const d = data.data;
      toast.success(
        `Synced ${d.created} new · ${d.skipped_existing} already there · ${d.failed} failed`,
      );
    } catch (e: any) {
      toast.error(e?.message || "Sync failed");
    } finally {
      setSyncing(false);
    }
  };

  const loadData = async () => {
    const { data: l } = await supabase
      .from("broadcast_logs")
      .select("*")
      .order("created_at", { ascending: false })
      .limit(20);
    setLogs((l as any) || []);

    const { count: uc } = await supabase
      .from("email_unsubscribes")
      .select("*", { count: "exact", head: true });
    setUnsubCount(uc || 0);

    const { count: pc } = await supabase
      .from("profiles")
      .select("*", { count: "exact", head: true });
    setProfilesCount(pc || 0);
  };

  useEffect(() => {
    loadData();
  }, []);

  const send = async (isTest: boolean) => {
    if (!subject.trim() || !body.trim()) {
      toast.error("Subject and body are required");
      return;
    }
    if (isTest && !testEmail.trim()) {
      toast.error("Enter a test email");
      return;
    }
    setSending(true);
    try {
      const { data, error } = await supabase.functions.invoke(
        "send-broadcast-email",
        {
          body: {
            subject,
            body_html: body,
            plan: plan === "all" ? null : plan,
            test_email: isTest ? testEmail.trim() : null,
          },
        },
      );
      if (error) throw error;
      if (!data?.ok) throw new Error(data?.error || "Failed");
      toast.success(
        isTest
          ? `Test sent to ${testEmail}`
          : `Sent to ${data.data.success} / ${data.data.recipients} recipients`,
      );
      if (!isTest) {
        setSubject("");
        setBody("");
        loadData();
      }
    } catch (e: any) {
      toast.error(e?.message || "Failed to send");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="animate-in fade-in duration-500 space-y-6">
      <div className="mb-6">
        <AdminPageHeader 
          title="Broadcast Emails"
          description="Send communications to platform users and segments."
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-card border border-border rounded-xl p-5 shadow-sm">
          <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">
            Total Users
          </p>
          <h3 className="text-2xl font-bold mt-1 tracking-tight text-foreground">{profilesCount}</h3>
        </div>
        <div className="bg-card border border-border rounded-xl p-5 shadow-sm">
          <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">
            Unsubscribed
          </p>
          <h3 className="text-2xl font-bold mt-1 tracking-tight text-foreground">{unsubCount}</h3>
        </div>
        <div className="bg-card border border-border rounded-xl p-5 shadow-sm">
          <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">
            Eligible recipients
          </p>
          <h3 className="text-2xl font-bold mt-1 tracking-tight text-foreground">
            {Math.max(0, profilesCount - unsubCount)}
          </h3>
        </div>
      </div>

      <div className="bg-card border border-border rounded-xl shadow-sm flex flex-col">
        <div className="px-5 py-4 border-b border-border flex flex-row items-center justify-between gap-4">
          <h3 className="text-lg font-bold flex items-center gap-2 text-foreground">
            <Users className="h-5 w-5" />
            Sync contacts to Resend
          </h3>
          <button 
            onClick={syncToResend} 
            disabled={syncing} 
            className="inline-flex items-center justify-center px-4 py-2 text-sm font-medium border border-border rounded-lg bg-background hover:bg-muted transition-colors disabled:opacity-50 disabled:pointer-events-none"
          >
            {syncing ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <Users className="h-4 w-4 mr-2" />
            )}
            Sync now
          </button>
        </div>
        <div className="p-5">
          <p className="text-sm text-muted-foreground">
            Pushes all eligible user emails into your Resend Audience so you can
            also send broadcasts from the Resend dashboard. Existing contacts are
            skipped automatically. Unsubscribed users are excluded.
          </p>
        </div>
      </div>

      <div className="bg-card border border-border rounded-xl shadow-sm flex flex-col">
        <div className="px-5 py-4 border-b border-border">
          <h3 className="text-lg font-bold flex items-center gap-2 text-foreground">
            <Mail className="h-5 w-5 text-primary" />
            Compose Broadcast
          </h3>
        </div>
        <div className="p-5 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="text-sm font-medium leading-none text-foreground">Audience</label>
              <select 
                value={plan} 
                onChange={(e) => setPlan(e.target.value)}
                className="flex h-10 w-full rounded-md border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary disabled:cursor-not-allowed disabled:opacity-50"
              >
                <option value="all">All users</option>
                <option value="free">Free plan</option>
                <option value="starter">Starter plan</option>
                <option value="pro">Pro plan</option>
                <option value="scale">Scale plan</option>
              </select>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium leading-none text-foreground">Test send to</label>
              <input
                type="email"
                placeholder="you@example.com"
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                className="flex h-10 w-full rounded-md border border-border bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary disabled:cursor-not-allowed disabled:opacity-50"
              />
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium leading-none text-foreground">Subject</label>
            <input
              type="text"
              placeholder="Big news from Bravax Forms..."
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              className="flex h-10 w-full rounded-md border border-border bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary disabled:cursor-not-allowed disabled:opacity-50"
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium leading-none text-foreground">Body (HTML allowed)</label>
            <textarea
              rows={12}
              placeholder="<h2>Hello!</h2><p>Your message...</p>"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              className="flex min-h-[80px] w-full rounded-md border border-border bg-background px-3 py-2 font-mono text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary disabled:cursor-not-allowed disabled:opacity-50"
            />
            <p className="text-xs text-muted-foreground">
              An unsubscribe link is automatically appended to every email
              (CAN-SPAM/GDPR compliant).
            </p>
          </div>

          <div className="flex flex-wrap gap-3 pt-2">
            <button
              onClick={() => send(true)}
              disabled={sending}
              className="inline-flex items-center justify-center rounded-lg h-10 px-4 py-2 text-sm font-medium border border-border bg-background hover:bg-muted transition-colors disabled:opacity-50 disabled:pointer-events-none"
            >
              {sending ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : null}
              Send test
            </button>
            <button 
              onClick={() => send(false)} 
              disabled={sending}
              className="inline-flex items-center justify-center rounded-lg h-10 px-4 py-2 text-sm font-medium bg-primary text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:pointer-events-none"
            >
              {sending ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Send className="h-4 w-4 mr-2" />
              )}
              Send to {plan === "all" ? "all" : plan} users
            </button>
          </div>
        </div>
      </div>

      <div className="bg-card border border-border rounded-xl shadow-sm flex flex-col">
        <div className="px-5 py-4 border-b border-border">
          <h3 className="text-lg font-bold text-foreground">Broadcast history</h3>
        </div>
        <div className="p-0">
          {logs.length === 0 ? (
            <p className="p-6 text-sm text-muted-foreground text-center">
              No broadcasts sent yet.
            </p>
          ) : (
            <div className="divide-y divide-border/50">
              {logs.map((l) => (
                <div
                  key={l.id}
                  className="p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4 hover:bg-muted/30 transition-colors"
                >
                  <div className="min-w-0">
                    <p className="font-medium text-foreground truncate">{l.subject}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {new Date(l.created_at).toLocaleString()} ·{" "}
                      {l.audience_filter?.plan || "all"} plan
                    </p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-muted text-muted-foreground">
                      {l.success_count}/{l.recipient_count} sent
                    </span>
                    {l.failed_count > 0 && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-destructive/10 text-destructive border border-destructive/20">
                        {l.failed_count} failed
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BroadcastEmails;
